'use client';

/**
 * SearchableSelect — a combobox: type to filter, arrows to move, Enter to
 * pick, Escape to close (WAI-ARIA combobox with a listbox popup).
 *
 * Client filtering over the options you pass:
 *
 *     <SearchableSelect
 *       aria-label="Assignee"
 *       options={members.map((m) => ({ value: m.id, label: m.name, description: m.email }))}
 *       value={assignee}
 *       onChange={setAssignee}
 *       placeholder="Choose a person"
 *     />
 *
 * Server search: pass `onQueryChange` (debounce it in your hook), feed the
 * results back as `options`, and set `loading` while they arrive. The
 * component then shows the options as given and does not filter them again.
 *
 * Label it: `aria-label`, or `id` + a FormField label (`htmlFor`).
 *
 * Typed text is never thrown away: with the list closed and nothing chosen,
 * the box keeps what was typed (muted) — see `searchableSelectText.ts`.
 * Pass `onCreate` to offer "+ Create “…”" as the last row when what is
 * typed is not an exact match (the "no clients match" dead end).
 */
import { Check, ChevronDown, Plus, X } from 'lucide-react';
import { type KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from 'react';

import { cn } from './cn';
import { comboboxShownText, isUnpickedText, shouldOfferCreate } from './searchableSelectText';

export interface SelectOption {
  value: string;
  label: string;
  description?: string | null;
  disabled?: boolean;
}

export interface SearchableSelectProps {
  options: readonly SelectOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  /** Server search: called on every keystroke; options are not filtered locally. */
  onQueryChange?: (query: string) => void;
  loading?: boolean;
  emptyText?: string;
  /** Show a clear (×) button when a value is chosen. Default true. */
  clearable?: boolean;
  disabled?: boolean;
  id?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  className?: string;
  /** Offer a "+ Create" row for the typed text; called with it (trimmed). */
  onCreate?: (query: string) => void;
  /** The create row's words. Default: `Create “<query>”`. */
  createLabel?: (query: string) => string;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Search…',
  onQueryChange,
  loading = false,
  emptyText = 'No matches.',
  clearable = true,
  disabled = false,
  id,
  className,
  onCreate,
  createLabel = (q) => `Create “${q}”`,
  ...aria
}: SearchableSelectProps) {
  const autoId = useId();
  const inputId = id ?? `${autoId}-input`;
  const listId = `${autoId}-list`;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const selected = options.find((o) => o.value === value) ?? null;

  const filtered = useMemo(() => {
    if (onQueryChange) return options;
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) || (o.description ?? '').toLowerCase().includes(q),
    );
  }, [options, query, onQueryChange]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [open]);

  // eslint-disable-next-line react-hooks/set-state-in-effect -- ported verbatim from user-panel (same pattern; see CLAUDE.md lint note)
  useEffect(() => setActiveIndex(0), [query]);

  const offerCreate = shouldOfferCreate({
    canCreate: Boolean(onCreate),
    query,
    loading,
    optionLabels: filtered.map((o) => o.label),
  });
  const rowCount = filtered.length + (offerCreate ? 1 : 0);
  const createIndex = offerCreate ? filtered.length : -1;

  const create = () => {
    if (!onCreate) return;
    setOpen(false);
    onCreate(query.trim());
  };

  const clearAll = () => {
    if (value != null) onChange(null);
    setQuery('');
    onQueryChange?.('');
  };

  const pick = (o: SelectOption) => {
    if (o.disabled) return;
    onChange(o.value);
    setQuery('');
    onQueryChange?.('');
    setOpen(false);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      else setActiveIndex((i) => Math.min(rowCount - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      if (open && activeIndex === createIndex) {
        e.preventDefault();
        create();
      } else if (open && filtered[activeIndex]) {
        e.preventDefault();
        pick(filtered[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        setOpen(false);
      }
    }
  };

  const optionId = (i: number) => `${autoId}-opt-${i}`;
  const textState = { open, query, selectedLabel: selected?.label ?? null };
  const shownText = comboboxShownText(textState);
  const unpicked = isUnpickedText(textState);

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <div className="relative">
        <input
          aria-activedescendant={open && activeIndex < rowCount ? optionId(activeIndex) : undefined}
          aria-autocomplete="list"
          aria-controls={listId}
          aria-describedby={aria['aria-describedby']}
          aria-expanded={open}
          aria-label={aria['aria-label']}
          autoComplete="off"
          className={cn(
            'h-10 w-full rounded-md border border-slate-300 bg-white pl-3 pr-16 text-sm text-fg placeholder:text-fg-muted focus:border-action focus:outline-none focus:ring-2 focus:ring-gold disabled:cursor-not-allowed disabled:bg-slate-50',
            unpicked && 'italic text-fg-secondary',
          )}
          disabled={disabled}
          id={inputId}
          placeholder={selected ? selected.label : placeholder}
          role="combobox"
          type="text"
          value={shownText}
          onChange={(e) => {
            setQuery(e.target.value);
            onQueryChange?.(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        <div className="absolute inset-y-0 right-2 flex items-center gap-1 text-fg-muted">
          {clearable && (selected || unpicked) && !disabled ? (
            <button
              aria-label={selected ? 'Clear selection' : 'Clear text'}
              className="rounded p-1 hover:bg-slate-100 hover:text-fg"
              type="button"
              onClick={clearAll}
            >
              <X aria-hidden size={13} strokeWidth={2} />
            </button>
          ) : null}
          <ChevronDown aria-hidden size={14} strokeWidth={1.75} />
        </div>
      </div>
      {open ? (
        <ul
          className="absolute left-0 right-0 top-full z-dropdown mt-1 max-h-60 overflow-y-auto rounded-lg border border-slate-200 bg-white p-1 shadow-3"
          id={listId}
          role="listbox"
        >
          {loading ? (
            <li className="px-2.5 py-2 text-sm text-fg-muted">Searching…</li>
          ) : (
            <>
              {filtered.length === 0 ? (
                <li className="px-2.5 py-2 text-sm text-fg-muted">{emptyText}</li>
              ) : null}
              {filtered.map((o, i) => (
                <li
                  key={o.value}
                  aria-disabled={o.disabled || undefined}
                  aria-selected={o.value === value}
                  className={cn(
                    'flex cursor-pointer items-start justify-between gap-2 rounded-md px-2.5 py-1.5',
                    i === activeIndex && 'bg-slate-100',
                    o.disabled && 'cursor-not-allowed opacity-50',
                  )}
                  id={optionId(i)}
                  role="option"
                  onClick={() => pick(o)}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setActiveIndex(i)}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm text-fg">{o.label}</span>
                    {o.description ? (
                      <span className="block truncate text-xs text-fg-muted">{o.description}</span>
                    ) : null}
                  </span>
                  {o.value === value ? (
                    <Check aria-hidden className="mt-0.5 shrink-0 text-gold-text" size={14} />
                  ) : null}
                </li>
              ))}
              {offerCreate ? (
                <li
                  aria-selected={false}
                  className={cn(
                    'flex cursor-pointer items-center gap-2 rounded-md border-t border-slate-100 px-2.5 py-1.5 text-sm font-medium text-gold-text',
                    activeIndex === createIndex && 'bg-slate-100',
                  )}
                  id={optionId(createIndex)}
                  role="option"
                  onClick={create}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setActiveIndex(createIndex)}
                >
                  <Plus aria-hidden size={14} strokeWidth={2} />
                  <span className="truncate">{createLabel(query.trim())}</span>
                </li>
              ) : null}
            </>
          )}
        </ul>
      ) : null}
    </div>
  );
}
